import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button, LoadingSkeleton } from "@narmi/design_system";
import { entities } from "byzantine";
import { bulkTemplateSchema } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { transformVersionToFormValues } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/api";
import Container from "../../../Container";
import VersionHistoryDrawer from "../Drawer/VersionHistoryDrawer";
import TemplateDetailsSection from "./TemplateDetailsSection";

type TemplateDetailsScreenProps = {
  isEditMode?: boolean;
};

const TemplateDetailsScreen = ({
  isEditMode = false,
}: TemplateDetailsScreenProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get("templateId");
  const versionId = searchParams.get("versionId");

  const form = entities.bulkTemplates.BulkTemplateForm.useForm();
  const { submitForm, setValidationSchema, setValues } = form;
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  const [templateData, setTemplateData] = useState<API.BulkTemplate | null>(
    null
  );

  const { loading: bootstrapLoading, load } =
    entities.bulkTemplates.bootstrap.useBootstrap();
  const { getTemplate, loading: templateLoading } =
    entities.bulkTemplates.useGetTemplate();
  const { getTemplateVersion, loading: versionLoading } =
    entities.bulkTemplates.useGetTemplateVersion();
  const { getTemplateVersions, loading: versionsLoading } =
    entities.bulkTemplates.useGetTemplateVersions();
  const recipients = entities.recipients.useRecipients();

  const recipientMap = useMemo(() => {
    const map: Record<string, API.Recipient> = {};
    recipients.forEach((recipient) => {
      map[recipient.id] = recipient;
    });
    return map;
  }, [recipients]);

  const loading =
    bootstrapLoading || templateLoading || versionLoading || versionsLoading;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setValidationSchema(bulkTemplateSchema);
  }, []);

  // edit mode (no versionId): load template's active version
  // triggered when navigating to /ach/edit?templateId=...
  // populates the form with the active version's data plus group metadata
  useEffect(() => {
    if (
      templateId &&
      !versionId &&
      Object.keys(recipientMap).length > 0 &&
      !form.dirty
    ) {
      getTemplate(templateId as API.BulkTemplateId, {
        onData: (response) => {
          setTemplateData(response);
          // for active_pending state, show the active version (not the pending one)
          const version =
            response.state === "active_pending"
              ? response.active_version
              : response.pending_version ?? response.active_version;
          if (version) {
            const formValues = transformVersionToFormValues(
              version,
              recipientMap
            );
            setValues(
              {
                ...formValues,
                versionCount: response.version_count,
                groupId: response.id,
              },
              false
            );
          } else {
            // if there is no active or pending version (e.g. rejected template)
            // fetch versions list and use the latest one
            getTemplateVersions(response.id as API.BulkTemplateId, {
              onData: (versions) => {
                if (versions.length === 0) return;
                const latestVersion = versions[0];
                const formValues = transformVersionToFormValues(
                  latestVersion,
                  recipientMap
                );
                setValues(
                  {
                    ...formValues,
                    versionCount: response.version_count,
                    groupId: response.id,
                  },
                  false
                );
              },
            });
          }
        },
      });
    }
  }, [templateId, versionId, recipientMap, getTemplate, setValues, form.dirty]);

  // load a specific version by versionId
  // 1. copy to new template: /ach/new?templateId=...&versionId=...
  //    populates the form with that version's data only
  // 2. copy to new version: /ach/edit?templateId=...&versionId=...
  //    populates the form with the version data, then also fetches the
  //    template group to get versionCount and groupId for submission
  useEffect(() => {
    if (
      versionId &&
      templateId &&
      Object.keys(recipientMap).length > 0 &&
      !form.dirty
    ) {
      getTemplateVersion(
        templateId as API.BulkTemplateId,
        versionId as API.BulkTemplateVersionId,
        {
          onData: (version) => {
            const formValues = transformVersionToFormValues(
              version,
              recipientMap
            );
            if (isEditMode) {
              getTemplate(templateId as API.BulkTemplateId, {
                onData: (response) => {
                  setTemplateData(response);
                  setValues(
                    {
                      ...formValues,
                      versionCount: response.version_count,
                      groupId: response.id,
                    },
                    false
                  );
                },
              });
            } else {
              // copy to new template: append "(Copy)" suffix to the name
              setValues(
                {
                  ...formValues,
                  title: l10n.getString("copy-to-new-template-name-suffix", {
                    name: formValues.title,
                  }),
                },
                false
              );
            }
          },
        }
      );
    }
  }, [
    versionId,
    templateId,
    recipientMap,
    getTemplateVersion,
    getTemplate,
    setValues,
    form.dirty,
    isEditMode,
  ]);

  const handleClickNext = async () => {
    const { success } = await submitForm();
    if (!success) {
      return;
    }

    navigate({ pathname: "payments", search: searchParams.toString() });
  };

  const handleViewVersionHistory = () => {
    setIsVersionHistoryOpen(true);
  };

  return (
    <>
      <Container maxWidth="640px">
        <LoadingSkeleton isLoading={loading}>
          <Row alignItems="center" justifyContent="space-between">
            <Row.Item>
              <h1>
                {isEditMode
                  ? l10n.getString("edit-template-header")
                  : l10n.getString("create-template-header")}
              </h1>
            </Row.Item>
            {isEditMode && (
              <Row.Item shrink>
                <Button
                  kind="plain"
                  onClick={handleViewVersionHistory}
                  label={l10n.getString("view-version-history-link")}
                />
              </Row.Item>
            )}
          </Row>
          <TemplateDetailsSection />
        </LoadingSkeleton>
        <div className="margin--bottom--xl margin--top--xl">
          <Row alignItems="center" justifyContent="end">
            <Row.Item shrink>
              <Button
                kind="negative"
                label={l10n.getString("button-back")}
                onClick={() => {
                  navigate(-1);
                }}
              />
            </Row.Item>
            <Row.Item shrink>
              <Button
                label={l10n.getString("button-next")}
                onClick={handleClickNext}
              />
            </Row.Item>
          </Row>
        </div>
      </Container>
      {isEditMode && templateId && templateData && (
        <VersionHistoryDrawer
          isOpen={isVersionHistoryOpen}
          onClose={() => setIsVersionHistoryOpen(false)}
          templateUuid={templateId as API.BulkTemplateId}
          groupState={templateData.state}
          activeVersionId={templateData.active_version?.id ?? null}
        />
      )}
    </>
  );
};

export default TemplateDetailsScreen;
